import { defineStore } from 'pinia';
import { http } from '@/system/http';
import { useServiceRates } from 'Modules/HelfoSettlements/resources/js/composables/serviceRates';
import Apps from '@/core/Apps';
import store from '@/system/store';
import { useTasksStore } from './tasksStore';

// Define view types as constants
export const SidebarViews = {
    // View screens
    CLIENT_BOOKING: 'client-booking',
    CLIENT_JOURNAL: 'client-journal',
    CLIENT_INVOICES: 'client-invoices',
    OTHER_BOOKING: 'other-booking',
    RECURRING_OTHER_BOOKING: 'recurring-other-booking',
    TASKS: 'tasks',
    
    // Create screens
    CREATE_CLIENT_BOOKING: 'create-booking',
    CREATE_CLIENT_JOURNAL: 'create-journal',
    CREATE_CLIENT_INVOICES: 'create-invoice',
    CREATE_CLIENT: 'create-client',
    EDIT_CLIENT: 'edit-client',
    CREATE_OTHER_BOOKING: 'create-other-booking',
    CREATE_CLIENT_BOOKING_INVOICE: 'create-client-booking-invoice',
};

// Layouts
export const SidebarLayouts = {
    TABBED: 'tabbed',
    SIMPLE: 'simple',
    OTHER: 'other',
};

// Map of views to their corresponding layouts
const viewLayoutMap = {
    [SidebarViews.CLIENT_BOOKING]: SidebarLayouts.TABBED,
    [SidebarViews.CLIENT_JOURNAL]: SidebarLayouts.TABBED,
    [SidebarViews.CLIENT_INVOICES]: SidebarLayouts.TABBED,
    [SidebarViews.TASKS]: SidebarLayouts.TABBED,
    [SidebarViews.CREATE_CLIENT_BOOKING]: SidebarLayouts.SIMPLE,
    [SidebarViews.CREATE_CLIENT_JOURNAL]: SidebarLayouts.SIMPLE,
    [SidebarViews.CREATE_CLIENT_INVOICES]: SidebarLayouts.SIMPLE,
    [SidebarViews.CREATE_CLIENT]: SidebarLayouts.SIMPLE,
    [SidebarViews.EDIT_CLIENT]: SidebarLayouts.SIMPLE,
    [SidebarViews.OTHER_BOOKING]: SidebarLayouts.OTHER,
    [SidebarViews.RECURRING_OTHER_BOOKING]: SidebarLayouts.OTHER,
    [SidebarViews.CREATE_OTHER_BOOKING]: SidebarLayouts.SIMPLE,
    [SidebarViews.CREATE_CLIENT_BOOKING_INVOICE]: SidebarLayouts.SIMPLE,
};

const serviceRatesComposable = useServiceRates();

export const useCalendarSidebarStore = defineStore('calendarSidebar', {
    state: () => ({
        isOpen: false,

        // Navigation state
        navigationStack: [],
        currentView: null,
        currentParams: null,
        
        // Booking data
        booking: null,

        // Journal data
        journals: [],
        journals_next_page_url: null,
        hasLoadedJournals: false,
        lastJournalRequestHash: null, // For tracking last request params

        // Invoice data
        invoiceNotes: [],
        noInvoiceEntries: true,
        invoiceParams: {
            search: '',
            list_filter: 'all',
            pageSize: 10,
            currentPage: 1,
            totalPages: null,
            sort: 'date',
            client_id: null,
            client_group_id: null,
        },
        lastInvoiceRequestHash: null, // For tracking last request params

        // Future appointments
        futureAppointments: [],
        futureAppointmentsNextPageURL: '',
        futureAppointmentsTotal: 0,
        

        // Service rates state from composable
        attachedServiceRates: serviceRatesComposable.attachedServiceRates,
        deductibleCode: serviceRatesComposable.deductibleCode,
        reimbursementTotal: serviceRatesComposable.reimbursementTotal,
        deductibleTotal: serviceRatesComposable.deductibleTotal,

        // UI state
        loading: false,
        error: null,

        // Specific loading indicators
        bookingLoading: false,
        journalsLoading: false,
        invoiceLoading: false,
    }),

    getters: {
        // Sidebar open/close
        isSidebarOpen: state => state.isOpen,
        // Get the layout type
        currentLayout: (state) => {
            if (!state.currentView) return SidebarLayouts.TABBED; // Default
            return viewLayoutMap[state.currentView] || SidebarLayouts.TABBED;
        },
        // Navigation getters
        currentViewType: state => state.currentView,
        canGoBack: state => state.navigationStack.length > 0,
        currentParamsValue: state => state.currentParams,
        // Add isHelfoSettlementsActive getter that checks if the app is enabled
        isHelfoSettlementsActive() {
            return store.getters['apps/isAppEnabled'](Apps.HelfoSettlements);
        },
        // Invoice finished loading
        invoiceFinishedLoading: (state) => {
            return !(state.invoiceParams.totalPages === 1 || 
                    state.invoiceParams.currentPage > state.invoiceParams.totalPages);
        },
        // Check if the journal is for a group
        isGroupJournal() {
            return !!this.booking?.client_group;
        },
        // Return the client type based on the booking data    
        clientOrGroup() {
            return this.booking?.client ? this.booking.client : this.booking?.client_group;
        },
        // Task store access
        tasksStore() {
            return useTasksStore();
        },
    },

    actions: {
        // Sidebar open/close
        openSidebar() {
            // set current view to invoices if not set
            if (!this.currentView) {
                this.currentView = SidebarViews.CLIENT_INVOICES;
            }
            this.isOpen = true;
        },

        closeSidebar() {
            this.isOpen = false;
            // Clear the navigation when closed
            this.resetState();
            // Reset tasks state
            this.tasksStore.resetState();
        },

        // Navigation actions
        navigateTo(viewType, params = null, hardReload = false) {
            // Reset navigation stack if going to a new view
            this.resetNavigation();
            
            // Set new view
            this.currentView = viewType;
            if (params) {
                this.currentParams = { ...params };
            }
            
            // Make sure sidebar is open
            this.openSidebar();
            
            // Load data based on view type if needed
            this._loadDataForCurrentView(hardReload);
        },

        navigateToSubView(viewType, params = null) {
            // Push current view to history stack if exists
            if (this.currentView) {
                this.navigationStack.push({
                    view: this.currentView,
                    params: this.currentParams
                });
            }

            // Set view and handle common navigation logic
            this._setCurrentView(viewType, params);
        },

        // Helper method for common navigation functionality
        _setCurrentView(viewType, params = null) {
            // Set new view
            this.currentView = viewType;
            if (params) {
                this.currentParams = { ...this.currentParams, ...params };
            }

            // Make sure sidebar is open
            this.openSidebar();

            // Load data based on view type if needed
            this._loadDataForCurrentView();
        },

        goBack() {
            if (!this.canGoBack) return false;

            // Get previous view from stack
            const prevView = this.navigationStack.pop();
            this.currentView = prevView.view;
            this.currentParams = prevView.params;

            // Load data for the view we're returning to
            this._loadDataForCurrentView();

            return true;
        },

        resetState() {
            this.navigationStack = [];
            this.currentView = null;
            this.currentParams = null;
            this.updateBooking(null);
            this.journal = null;
        },

        resetNavigation() {
            this.navigationStack = [];
            this.currentView = null;
            this.currentParams = null;
        },

        setLoading(loading) {
            this.loading = loading;
        },

        handleSidebarCancel(callback = null) {
            // Check if there's a previous view in the navigation stack
            if (this.canGoBack) {
                // Go back to the previous view
                this.goBack();
            } else {
                // No previous view, close the sidebar
                this.closeSidebar();
            }

            if (callback && typeof callback === 'function') {
                callback();
            }
        },

        discardBooking() {
            let currentView;
            if (this.currentView === SidebarViews.CLIENT_INVOICES || this.currentView === SidebarViews.TASKS) {
                // If the current view is Invoices or Tasks, discard the booking and load all invoices or tasks
                currentView = this.currentView;
                this.resetState();
                this.currentView = currentView; // Restore current view after reset
                this._loadDataForCurrentView(true); // Force reload data
            } else {
                // Else, close the sidebar
                this.closeSidebar();
            }
        },
        
        // Data loading actions
        async _loadDataForCurrentView(hardReload = false) {
            if (!this.currentView) return;

            switch (this.currentView) {
                case SidebarViews.CLIENT_BOOKING:
                    if (this.currentParams?.bookingId) {
                        await this.fetchBooking(this.currentParams.bookingId, hardReload);
                    }
                    break;
                case SidebarViews.CLIENT_INVOICES:
                    // Load invoices for the current client/group
                    await this.fetchInvoices();
                    break;
                case SidebarViews.TASKS:
                    // Load tasks for the current client/group
                    const clientId = this.clientOrGroup?.id;
                    this.tasksStore.getTasks(false, false, clientId);
                    break;
            }
        },

        // Fetch booking data
        async fetchBooking(bookingId, hardReload = false) {
            // Skip if already loading
            if (this.bookingLoading) return;

            // Skip loading if it's the same booking
            if (this.booking && bookingId === this.booking.id && !hardReload) {
                return this.booking;
            }

            this.loading = true;
            this.bookingLoading = true;
            this.error = null;

            try {
                const response = await http.get(`/app/booking/${bookingId}`);
                this.updateBooking(response.data);
                
                // Call additional methods matching original functionality
                this.getFutureAppointments();

                // Conditionally reload service rates
                if (this.isHelfoSettlementsActive) {
                    this.reloadServiceRatesFromBooking(this.booking.id);
                }

                this.bookingLoading = false;
                return this.booking;
            } catch (error) {
                this.error = error;
                this.loading = false;
                this.bookingLoading = false;
                throw error;
            }
        },

        // Fetch invoice data
        async fetchInvoices(fromLoadMore = false) {
            // Skip if already loading or pagination is complete
            if (this.invoiceLoading || 
                    (this.invoiceParams.totalPages && this.invoiceParams.currentPage > this.invoiceParams.totalPages)) {
                return;
            }
            
            // Create request params object
            const requestParams = {
                filter: this.invoiceParams.list_filter,
                page: this.invoiceParams.currentPage,
                show: this.invoiceParams.pageSize,
                search: this.invoiceParams.search,
                client_id: this.invoiceParams.client_id,
                client_group_id: this.invoiceParams.client_group_id,
                sort: this.invoiceParams.sort,
            };
            
            // Generate hash for request to check for duplicates
            const requestHash = this.booking?.id || 'global';
            
            // Skip if requesting the same data
            if (this.lastInvoiceRequestHash === requestHash && fromLoadMore === false) {
                return;
            }
            
            // Remember this request hash
            this.lastInvoiceRequestHash = requestHash;
            
            // Reset notes if not loading more
            if (!fromLoadMore) {
                this.invoiceNotes = [];
            }
            
            this.invoiceLoading = true;
            
            try {
                const { data } = await http.get('/app/invoices/get', { params: requestParams });
                
                // Update notes with new or additional data
                this.invoiceNotes = fromLoadMore ? [...this.invoiceNotes, ...data.notes] : data.notes;
                
                // Update pagination and filtering state
                this.invoiceParams.totalPages = data.pageCount;
                this.invoiceParams.currentPage++;
                this.noInvoiceEntries = this.invoiceNotes.length === 0;
            } catch (error) {
                console.error('Error fetching invoices:', error);
                this.noInvoiceEntries = true;
            } finally {
                this.invoiceLoading = false;
            }
        },

        // Get future appointments for the current client
        async getFutureAppointments() {
            if (!this.booking) return;

            const id = this.booking.client
                ? this.booking.client.id
                : this.booking.client_group_id;

            if (!id) return;

            try {
                const response = await http.get(`/app/client/${id}/future-appointments`, {
                    params: { include_cancelled: false }
                });

                this.futureAppointments = response.data.data;
                this.futureAppointmentsNextPageURL = response.data.next_page_url;
                this.futureAppointmentsTotal = response.data.total;
            } catch (error) {
                console.error('Error fetching future appointments:', error);
            } finally {
                this.loading = false;
            }
        },

        // Fetch journal data
        getJournalEntries(isLoadMore = false, hardReload = false) {
            // Generate hash for request to check for duplicates
            const requestHash = this.booking.id;
            
            // Skip if requesting the same data
            if (this.lastJournalRequestHash === requestHash  && !hardReload && !isLoadMore) {
                return;
            }
            
            // Remember this request hash
            this.lastJournalRequestHash = requestHash;

            this.journalsLoading = true;
            this.hasLoadedJournals = true;
    
            const url = isLoadMore ? 
                this.journals_next_page_url : 
                '/app/journal' + (this.booking.id ? `?bookingId=${this.booking.id}` : '');
    
            return http.get(url, {
                params: { clientOrGroupId: this.clientOrGroup.id, forGroup: this.isGroupJournal },
            })
            .then(({ data }) => {
                // Filter out journals that already exist
                const existingJournalIds = this.journals.map(journal => journal.id);
                
                if (isLoadMore) {
                    const newJournals = data.data.filter(journal => !existingJournalIds.includes(journal.id));
                    this.journals = [...this.journals, ...newJournals];
                } else {
                    this.journals = data.data;
                }

                this.journals_next_page_url = data.links.next;
                this.journalsLoading = false;
            })
            .catch(({ error, errorBag }) => {
                this.journalsLoading = false;
                this.hasLoadedJournals = false;
            });
        },

        resetJournals() {
            this.journals = [];
            this.journals_next_page_url = null;
            this.hasLoadedJournals = false;
            this.journalsLoading = false;
            this.lastJournalRequestHash = null;
        },

        updateJournalContent(journalId, content) {
            const journalIndex = this.journals.findIndex(journal => journal.id === journalId);
            if (journalIndex !== -1) {
                // Create a new journal object to maintain reactivity
                const updatedJournal = { ...this.journals[journalIndex] };
                updatedJournal.content = content;
                
                // Replace the journal in the array
                this.journals.splice(journalIndex, 1, updatedJournal);
            }
        },

        markJournalFavorite(journalId, markedAsFavorite) {
            const journalIndex = this.journals.findIndex(journal => journal.id === journalId);

            if (journalIndex !== -1) {
                // Create a new journal object to maintain reactivity
                const updatedJournal = { ...this.journals[journalIndex] };
                updatedJournal.favorite = markedAsFavorite;
                
                // Replace the journal in the array
                this.journals.splice(journalIndex, 1, updatedJournal);
            }
        },

        removeJournal(journalId) {
            const journalIndex = this.journals.findIndex(journal => journal.id === journalId);
            if (journalIndex !== -1) {
                // Remove the journal from the array
                this.journals.splice(journalIndex, 1);
            }
        },

        reloadServiceRatesFromBooking(bookingId) {
            serviceRatesComposable.reloadServiceRatesFromBooking(bookingId);
        },

        // Data updating actions

        updateBookingInvoiceLine(invoiceline) {
            this.booking.invoiceline = invoiceline;
        },

        updateBooking(booking) {
            this.booking = booking;

            // Update invoiceParams when booking changes
            this.lastInvoiceRequestHash = null; // Reset last request hash
            this.invoiceParams.currentPage = 1;
            this.invoiceParams.client_id = booking?.client?.id;
            this.invoiceParams.client_group_id = booking?.client_group_id;

            // Update journal state when booking changes
            this.resetJournals();
            // Reset tasks state when booking changes
            this.tasksStore.resetState();
        },

        updateClient(client) {
            this.booking.client.name = client.name;
            this.booking.client.email = client.email;
            this.booking.client.phone = client.phone;
            this.booking.client.tags = client.tags;
            this.booking.client.address = client.address;
            this.booking.client.city = client.city;
            this.booking.client.zip = client.zip;
            this.booking.client.cpr = client.cpr;
            this.booking.client.requires_patient_travel = client.requires_patient_travel;
            this.booking.client.notes = client.notes;
        },

        updateTreatment(treatment) {
            this.booking.treatment = treatment;
        },

        // Handle online meeting updates and deletions
        handleOnlineMeeting(meeting, action = 'update') {
            if (this.booking && meeting.booking_id === this.booking.id) {
                this.booking.online_meeting = action === 'update' ? { ...meeting } : null;
            }
        },

        // Handle survey request addition
        handleSurveyRequestAdded(surveyRequest) {
            if (this.booking && this.booking.id === surveyRequest.booking_id) {
                if (!this.booking.survey_requests) {
                    this.booking.survey_requests = [];
                }
                this.booking.survey_requests.push(surveyRequest);
            }
        },

        // Handle survey request deletion
        handleSurveyRequestDeleted(surveyRequest) {
            if (this.booking && this.booking.id === surveyRequest.booking_id) {
                this.booking.survey_requests = (this.booking.survey_requests || [])
                    .filter(sr => sr.id !== surveyRequest.id);
            }
        },

        // Handle client diagnosis creation
        handleClientDiagnosisCreated(diagnosis) {
            if (this.booking) {
                this.booking.client_diagnoses.push(diagnosis);
            }
        },

        // Handle client diagnosis deletion
        handleClientDiagnosisDeleted() {
            if (this.booking) {
                // A diagnosis can only be deleted from the calendar if there's only one diagnosis
                this.booking.client_diagnoses = [];
            }
        },

        // Handle client diagnosis update
        handleClientDiagnosisUpdated(diagnosis) {
            if (this.booking) {
                // A diagnosis can only be updated from the calendar if there's only one diagnosis
                this.booking.client_diagnoses[0] = diagnosis;
            }
        }
    }
});
